import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { MathUtils, Vector3 } from 'three';

const MOVE_SPEED = 25; // world units / second
const ROTATE_SPEED = 1.3; // radians / second
const WORLD_UP = new Vector3(0, 0, 1);
const MOVE_KEYS = new Set(['w', 'a', 's', 'd', 'q', 'e']);

interface OrbitLike {
  target: Vector3;
  update: () => void;
}

function keyToken(e: KeyboardEvent): string | null {
  const k = e.key.toLowerCase();
  if (MOVE_KEYS.has(k)) return k;
  if (k.startsWith('arrow')) return k; // arrowleft/right/up/down
  return null;
}

/**
 * Keyboard camera navigation, composed with OrbitControls:
 *   W A S D — translate across the ground plane (XY), relative to view heading
 *   Q / E   — move down / up the vertical axis (Z)
 *   ← → ↑ ↓ — rotate the view (orbit azimuth / elevation) around the target
 *
 * Continuous (hold-to-move) and framerate-independent via `delta`. Translation
 * moves camera AND orbit target together; rotation spins the camera offset
 * about the target using a Z-up spherical (Three's Spherical is Y-up).
 * `controls.update()` preserves both, so mouse-orbit and keys coexist.
 */
export function KeyboardControls() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as unknown as OrbitLike | null;
  const held = useRef(new Set<string>());

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return; // let the timeline slider keep its keys
      const t = keyToken(e);
      if (!t) return;
      held.current.add(t);
      e.preventDefault();
    };
    const onUp = (e: KeyboardEvent) => {
      const t = keyToken(e);
      if (t) held.current.delete(t);
    };
    const clear = () => held.current.clear(); // avoid stuck keys on window blur
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', clear);
    };
  }, []);

  useFrame((_, delta) => {
    const keys = held.current;
    if (keys.size === 0 || !controls) return;
    const target = controls.target;

    // --- Translation: WASD across the ground plane, QE along vertical ---
    const forward = new Vector3().subVectors(target, camera.position);
    forward.z = 0; // project heading onto the ground
    if (forward.lengthSq() > 1e-6) forward.normalize();
    const right = new Vector3().crossVectors(forward, WORLD_UP).normalize();

    const move = new Vector3();
    if (keys.has('w')) move.add(forward);
    if (keys.has('s')) move.sub(forward);
    if (keys.has('d')) move.add(right);
    if (keys.has('a')) move.sub(right);
    if (keys.has('e')) move.z += 1;
    if (keys.has('q')) move.z -= 1;
    if (move.lengthSq() > 0) {
      move.normalize().multiplyScalar(MOVE_SPEED * delta);
      camera.position.add(move);
      target.add(move);
    }

    // --- Rotation: arrows orbit the camera around the target (Z-up spherical) ---
    let dAz = 0;
    let dPol = 0;
    if (keys.has('arrowleft')) dAz += 1;
    if (keys.has('arrowright')) dAz -= 1;
    if (keys.has('arrowup')) dPol -= 1;
    if (keys.has('arrowdown')) dPol += 1;
    if (dAz !== 0 || dPol !== 0) {
      const offset = new Vector3().subVectors(camera.position, target);
      const r = offset.length();
      const theta = Math.atan2(offset.y, offset.x) + dAz * ROTATE_SPEED * delta;
      const phi = MathUtils.clamp(
        Math.acos(MathUtils.clamp(offset.z / r, -1, 1)) + dPol * ROTATE_SPEED * delta,
        0.05,
        Math.PI - 0.05,
      );
      const sinPhi = Math.sin(phi);
      offset.set(r * sinPhi * Math.cos(theta), r * sinPhi * Math.sin(theta), r * Math.cos(phi));
      camera.position.copy(target).add(offset);
    }

    controls.update();
  });

  return null;
}
